import React from "react";

import type { LaneRendererProps } from "../lane.types";
import { itemVisualDensity, productTone, readNumberMeta, readStringMeta, visibleLaneItems } from "../product/rendering";

function stateChip(label: string, tone: "violet" | "amber" | "rose" | "slate") {
  const className =
    tone === "violet"
      ? "border-violet-300/18 bg-violet-200/90 text-violet-950"
      : tone === "amber"
        ? "border-amber-700/16 bg-amber-50/92 text-amber-900"
        : tone === "rose"
          ? "border-rose-700/16 bg-rose-50/92 text-rose-900"
          : "border-slate-700/16 bg-slate-50/92 text-slate-900";

  return <span className={["rounded-full border px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em]", className].join(" ")}>{label}</span>;
}

export default function EventLaneRenderer({ lane, items, viewport }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const visibleItems = visibleLaneItems(items, viewport);

  return (
    <div className="relative h-full min-h-[48px]" data-lane-class="event" data-lane-id={lane.id} data-lane-items={items.length}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-violet-200/10" />
      {visibleItems.length > 0 ? (
        visibleItems.map(({ item, leftPx, widthPx }) => {
          const density = itemVisualDensity(widthPx);
          const label = item.itemType === "event" ? item.eventKey : item.id;
          const status = readStringMeta(item, "status") ?? readStringMeta(item, "mode") ?? item.sourceType ?? null;
          const actor = readStringMeta(item, "actor") ?? readStringMeta(item, "sourceLabel") ?? readStringMeta(item, "characterId");
          const strength = item.itemType === "event" ? item.strength ?? readNumberMeta(item, "strength") : readNumberMeta(item, "strength");

          return (
            <div
              key={item.id}
              data-visual-lane-item-id={item.id}
              className="absolute top-1/2 h-10 -translate-y-1/2 overflow-hidden rounded-xl border border-violet-300/24 bg-[linear-gradient(135deg,rgba(196,181,253,0.20),rgba(91,33,182,0.14),rgba(2,6,23,0.20))] px-3 shadow-[0_8px_20px_rgba(124,58,237,0.10)]"
              style={{ left: leftPx, width: widthPx }}
              title={label}
            >
              <div className="absolute inset-x-2 bottom-[6px] flex h-5 items-center gap-[3px] opacity-80">
                {Array.from({ length: Math.max(3, Math.min(10, Math.round(widthPx / 18))) }, (_, index) => (
                  <div
                    key={index}
                    className="rounded-full bg-violet-100/28"
                    style={{
                      width: index % 2 === 0 ? 6 : 4,
                      height: `${Math.max(24, Math.round(((strength ?? 0.45) * 100) - ((index % 3) * 10)))}%`,
                    }}
                  />
                ))}
              </div>
              <div className="relative flex h-full items-center justify-between gap-2">
                <div className="min-w-0">
                  {density !== "micro" ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-violet-50">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 overflow-hidden">
                        {actor ? stateChip(actor, "violet") : null}
                        {status ? stateChip(status, status === "warning" ? "amber" : "slate") : null}
                        {typeof strength === "number" ? stateChip(`${Math.round(strength * 100)}%`, strength >= 0.75 ? "rose" : "amber") : null}
                      </div>
                    </>
                  ) : (
                    <div className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-50/88">Evt</div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="grid h-full place-items-center px-3">
          <div className={["rounded-full border px-3 py-1 text-[10px] font-semibold", tone.emptyClassName].join(" ")}>No events in view</div>
        </div>
      )}
    </div>
  );
}